import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage } from "src/lib/utils";
import client from "../../api";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { Button, Table, Drawer, Tabs, Input, Tag, Space, Modal } from "antd";
import { debounce } from "lodash";

const ServiceIncidents = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const { canView, canList } = usePermissions(
    permissionConstants.SERVICE_INCIDENTS
  );
  const canViewIncidents = canView || canList;

  const [dataSource, setDataSource] = useState([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");

  const { mutate: getTemplatesList, isLoading } = useMutation(
    (params: { page: number; limit: number; q?: string }) =>
      client.serviceIncidents.getAllIncidentsList(params),

    {
      onSuccess: (data) => {
        setDataSource(
          data.items.map((item: any) => ({
            ...item,
          })),
        );
        setTotalItems(data.totalcount || 0);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") setAlertText(NetworkDisconnectMsg);
        else if (error.response)
          setAlertText(getErrorMessage(error.response.data));
        else setAlertText(APISomethingWrongMsg);
        setIsAlertOpened(true);
      },
    },
  );

  const debouncedSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setCurrentPage(1); // reset page when searching
        setSearchText(value.trim());
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (!isMounted || !canViewIncidents) return;

    getTemplatesList({
      page: currentPage,
      limit: pageSize,
      q: searchText || undefined,
    });
  }, [isMounted, canViewIncidents, currentPage, pageSize, searchText]);

  const columns: ColumnsType<any> = [
    { title: "Service Name", dataIndex: "service_name", key: "service_name" },
    { title: "Severity ", dataIndex: "severity", key: "severity" },
    {
      title: "Message",
      dataIndex: "details",
      key: "details",
      render: (details: any) => (
        <div className="flex flex-col gap-1">{details?.message}</div>
      ),
    },

    // {
    //   title: "Email",
    //   dataIndex: "email",
    //   key: "email",
    //   render: (email, record) => (
    //     <ToggleSwitch
    //       type={`${record.id}-email`}
    //       value={!!email}
    //       onValueChanged={(_: any, newValue: boolean) =>
    //         handleToggleChange(record, "email", newValue)
    //       }
    //     />
    //   ),
    // },
    // {
    //   title: "SMS",
    //   dataIndex: "sms",
    //   key: "sms",
    //   render: (sms, record) => (
    //     <ToggleSwitch
    //       type={`${record.id}-sms`}
    //       value={!!sms}
    //       onValueChanged={(_: any, newValue: boolean) =>
    //         handleToggleChange(record, "sms", newValue)
    //       }
    //     />
    //   ),
    // },
    // {
    //   title: "Slack",
    //   dataIndex: "slack",
    //   key: "slack",
    //   render: (slack, record) => (
    //     <ToggleSwitch
    //       type={`${record.id}-slack`}
    //       value={!!slack}
    //       onValueChanged={(_: any, newValue: boolean) =>
    //         handleToggleChange(record, "slack", newValue)
    //       }
    //     />
    //   ),
    // },
    {
      title: "Updated at",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}{" "}
          </p>
        </div>
      ),
    },
  ];
  return (
    <>
      <div className="flex justify-between items-center p-2 sm:p-4">
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium">
          Services Incident
        </p>
        <input
          type="text"
          placeholder="Search by service name"
          value={searchInput}
          onChange={(e) => {
            const value = e.target.value;
            setSearchInput(value); // update UI instantly
            debouncedSearch(value); // call API after delay
          }}
          className="border px-3 py-2 rounded-md w-[250px] focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {!canViewIncidents ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view service incidents.
          </p>
        </div>
      ) : (
        <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            loading={isLoading}
            showSorterTooltip={false}
            columns={columns}
            dataSource={dataSource}
            pagination={{
              total: totalItems,
              current: currentPage,
              onChange: (page, size) => setCurrentPage(page),
              position: ["bottomCenter"],
              showSizeChanger: false,
            }}
          />
        </div>
      )}
    </>
  );
};

export default ServiceIncidents;
